import App from '@/app/App'
import { PageDocument, SectionSlice, StickersSlice } from '../../prismicio-types'
import { Embed } from './Embed'
import { Stickers } from './Stickers'
import { Logo } from './Logo'
import { Title } from './Title'
import { Copy } from './Copy'
import { Columns } from './Columns'
import { Accordion } from './Accordion'
import { List } from './List'
import styles from './Section.module.css'
import { LinkList } from './LinkList'
import { Aside } from './Aside'
import { Image } from './Image'
import { Video } from './Video'
import { Form } from './Form'
import { ShortLang } from '@/const'

type Slice = PageDocument['data']['body'][number]

export type SectionProps = SectionSlice & {
	inner: Slice[]
	stickers?: StickersSlice
}

type Options = {
	node: HTMLElement
	app: App
}

export class Section {
	private readonly node: HTMLElement
	private readonly app: App
	private readonly elements: HTMLElement[]
	private readonly id: string

	constructor({ node, app }: Options) {
		this.onIntersect = this.onIntersect.bind(this)
		this.onHashChange = this.onHashChange.bind(this)
		this.node = node
		this.app = app
		this.id = this.node.id
		this.node.removeAttribute('id')
		this.elements = Array.from(
			node.querySelectorAll(
				`.${styles.Copy}, .${styles.Columns}, .${styles.Accordion}, .${styles.List}, .${styles.Aside}, .${styles.LinkList}, .${styles.Image}, .${styles.Video}, .${styles.Embed}, .${styles.Form}`
			) as NodeListOf<HTMLElement>
		)
		this.app.on('intersect', this.onIntersect)
		window.addEventListener('hashchange', this.onHashChange)
	}

	onHashChange() {
		const id = window.location.hash.replace('#', '')
		if (this.id === id) {
			this.node.scrollIntoView({ behavior: 'smooth' })
		}
	}

	async onIntersect(entries: IntersectionObserverEntry[]) {
		for (let i = 0; i < entries.length; i++) {
			if (entries[i].isIntersecting) {
				for (let ii = 0; ii < this.elements.length; ii++) {
					if (this.elements[ii] === entries[i].target) {
						this.app.intersectionObserver.unobserve(this.elements[ii])
						this.elements[ii].classList.add(styles.IsVisible)
					}
				}
			}
		}
	}

	async show() {
		this.elements.forEach((element) => this.app.intersectionObserver.observe(element))
	}

	dispose() {
		window.removeEventListener('hashchange', this.onHashChange)
		this.app.removeListener('intersect', this.onIntersect)
		this.elements.forEach((element) => this.app.intersectionObserver.unobserve(element))
	}

	static render(props: SectionProps, fixed = false, lang: ShortLang = 'de') {
		const {
			primary: { sticky, fullscreen, no_padding, anchor },
			inner,
			stickers
		} = props

		const uid = 'uid' in anchor ? anchor.uid : undefined

		return /* HTML */ `
			<section
				class="${styles.Main} ${sticky ? styles.IsSticky : ''} ${fixed ? styles.IsFixed : ''}  ${
					fullscreen ? styles.Fullscreen : ''
				}"
				id="${uid ? uid : ''}"
			>
				<div class="${styles.Inner} ${no_padding ? styles.NoPadding : ''} ">
					${inner
						.map((slice, index) => {
							const delay = `transition-delay: ${250 + index * 100}ms`

							switch (slice.slice_type) {
								case 'logo': {
									return `<div class=${styles.Logo}>${Logo.render()}</div>`
								}
								case 'title': {
									return `<div class=${styles.Title}>${Title.render(slice)}</div>`
								}
								case 'copy': {
									return `<div class="${styles.Copy}" style="${delay}">${Copy.render(slice)}</div>`
								}
								case 'aside': {
									return `<div class="${styles.Aside}" style="${delay}">${Aside.render(slice)}</div>`
								}
								case 'columns': {
									return `<div class="${styles.Columns}" style="${delay}">${Columns.render(
										slice
									)}</div>`
								}
								case 'accordion': {
									return `<div class="${styles.Accordion}" style="${delay}">${Accordion.render(
										slice
									)}</div>`
								}
								case 'list': {
									return `<div class="${styles.List}" style="${delay}">${List.render(slice)}</div>`
								}
								case 'image': {
									return `<div class="${styles.Image}" style="${delay}">${Image.render(slice)}</div>`
								}
								case 'embed': {
									return `<div class="${styles.Embed}" style="${delay}">${Embed.render(slice.primary)}</div>`
								}
								case 'video': {
									return `<div class="${styles.Video}" style="${delay}">${Video.render(slice)}</div>`
								}
								case 'form': {
									return `<div class="${styles.Form}" style="${delay}">${Form.render(slice, lang)}</div>`
								}
								case 'link_list': {
									return `<div class="${styles.LinkList}" style="${delay}">${LinkList.render(
										slice
									)}</div>`
								}
							}
						})
						.join('')}
				</div>
				${stickers ? `<div class="${styles.Stickers}">${Stickers.render(stickers, fixed)}</div>` : ''}
			</section>
		`
	}
}
